import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useContract } from '../api/hooks';
import { money2 } from '../lib/data';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import AgentChip from '../components/AgentChip';
import Confidence from '../components/Confidence';
import StatusPill from '../components/StatusPill';
import { ArrowLeft, Save, Cpu } from 'lucide-react';
import { toast } from '../lib/aiToast';

export default function ContractDetail() {
  const { id } = useParams();
  const { data: contract = {} } = useContract(id);
  const [items, setItems] = useState([]);
  const [reviewed, setReviewed] = useState(false);

  // The contract (and its fee schedule) loads async; seed local editable state
  // once it arrives.
  useEffect(() => {
    setItems(contract.feeSchedule || []);
    setReviewed(contract.parseStatus === 'reviewed');
  }, [contract.id, contract.parseStatus, contract.feeSchedule]);

  const updateRate = (idx, val) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, rate: parseFloat(val) || 0 } : it)));

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/app/contracts"><Button variant="ghost" size="icon"><ArrowLeft className="h-4 w-4" /></Button></Link>
        <div className="flex-1">
          <div className="flex items-center gap-2.5">
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">{contract.payerName}</h1>
            <StatusPill status={reviewed ? 'done' : 'awaiting_review'} label={reviewed ? 'Live' : 'Needs review'} />
          </div>
          <div className="text-sm text-slate-500 mono">{contract.fileName} · {contract.lineCount} lines</div>
        </div>
        {!reviewed && <Button className="bg-brand-600 hover:bg-brand-700 text-white" onClick={() => { setReviewed(true); toast.success('Approved — fee schedule is now live'); }}><Save className="h-4 w-4 mr-1.5" />Approve & go live</Button>}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="dh-card p-5">
          <div className="text-sm font-semibold text-slate-900 mb-3">Extracted terms</div>
          <dl className="space-y-2.5 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Effective</dt><dd className="text-slate-900 tnum">{contract.effectiveDate}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Term</dt><dd className="text-slate-900 tnum">{contract.termDate || '—'}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Status</dt><dd>{reviewed ? 'Live' : 'Pending review'}</dd></div>
          </dl>
          <div className="mt-4 text-xs uppercase font-semibold text-slate-500 mb-2">Included plans</div>
          <ul className="space-y-1 text-sm">{(contract.includedPlans || []).map((p) => <li key={p} className="text-slate-800">• {p}</li>)}</ul>
          <div className="ai-surface p-3 mt-4">
            <div className="flex items-center justify-between mb-1"><AgentChip agent="verification" label="Contract" size="xs" /><Confidence value={0.92} label="extract" /></div>
            <p className="text-xs text-slate-700">Pulled effective dates, term, fee schedule, and plan inclusions from contract pages 4, 12, and 23. Review rates before going live.</p>
          </div>
        </div>

        <div className="dh-card-flush lg:col-span-2">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Cpu className="h-4 w-4 text-brand-500" />Fee schedule</div>
            <div className="text-xs text-slate-500 tnum">{items.length} CPT lines</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 bg-slate-50/70"><tr><th className="text-left font-medium px-5 py-2.5">CPT</th><th className="text-left font-medium px-5 py-2.5">Description</th><th className="text-right font-medium px-5 py-2.5">Contracted rate</th></tr></thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx} className="border-t border-slate-100">
                    <td className="px-5 py-2.5 mono text-xs">{it.cpt}</td>
                    <td className="px-5 py-2.5 text-slate-700">{it.description}</td>
                    <td className="px-5 py-2.5 text-right">
                      {reviewed ? <span className="tnum text-slate-900">{money2(it.rate)}</span> : <Input className="w-28 text-right inline-block h-8 tnum" value={it.rate} onChange={(e) => updateRate(idx, e.target.value)} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
